// ------------------------------------------------------------------------------
// UploadFileDragger - 由 tiangong 项目提取公共
// 来源于 packages/tiangong/src/components/UploadFileDragger/index.tsx
// author: mudas
// created: 2025/2/12
// ------------------------------------------------------------------------------

import React, { FC, useRef } from 'react';
import useAntStaticComponent from 'common/hooks/useAntStaticComponent';
import { v4 as uuid } from 'uuid';
import { isNil } from 'lodash-es';
import { blobToFile, dataURItoBlob, isImageFile, isMov, isMp4, isVideoFile } from 'common/utils';

import { RcFile } from 'antd/es/upload';
import { UploadRequestOption } from 'rc-upload/es/interface';
import { GetProp, Upload, UploadFile } from '@fuxi/eevee-ui';
import { DraggerUploadFile, FileACL, UploadFileObjType, UploadProps } from 'common/types';
import { UploadManager } from 'common/hooks/upload/UploadManager';
import { MAX_IMAGE_SIZE, MAX_VIDEO_SIZE, MAX_WIDTH, MAX_UPLOAD_IMAGE_W, MAX_UPLOAD_VIDEO_W } from './types';

import service from 'common/service';

const { Dragger } = Upload;

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

export interface UploadFileDraggerProps extends Omit<UploadProps, 'value' | 'onChange'> {
  id?: string;
  value?: DraggerUploadFile[];
  onChange?: (file: DraggerUploadFile, fileList: DraggerUploadFile[]) => void;
  enableVideo?: boolean; // 是否允许上传视频
  shouldGenerateThumb?: boolean;

  maxImageSize?: number;
  maxVideoSize?: number;
  maxImageWidth?: number;
  maxVideoWidth?: number;
  maxFilesCount?: number;
  namespace?: string;

  // children?: UploadProps['children'];
  //
  // beforeUpload?: UploadProps['beforeUpload'];
  // className?: UploadProps['className'];
  // accept?: UploadProps['accept'];
}

export enum ErrorType {
  ThumbnailFail,
  VideoCodec,
  UploadFail,
}

const UploadFileDragger: FC<UploadFileDraggerProps> = (
  {
    id,
    value = [],
    onChange,
    shouldGenerateThumb = true,
    children,
    maxImageSize = MAX_IMAGE_SIZE,
    maxVideoSize = MAX_VIDEO_SIZE,
    maxFilesCount,
    beforeUpload: customBeforeUpload,
    // className,
    // accept,
    enableVideo = true,
    namespace,
    maxImageWidth = MAX_UPLOAD_IMAGE_W,
    maxVideoWidth = MAX_UPLOAD_VIDEO_W,
    ...otherUploadProps
  },
  ref,
) => {
  const { message: AntdMessage } = useAntStaticComponent();
  const um = useRef<UploadManager>(
    new UploadManager({
      autoUpload: true,
    }),
  );

  const cropImgByCanvas = async (url: string, fileName: string) => {
    return new Promise<UploadFileObjType>((resolve, reject) => {
      // crop img by height or width is smaller
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        // 获取图片原始尺寸
        const width = img.width;
        const height = img.height;

        // 确定剪裁和压缩后的目标尺寸
        const minSide = Math.min(width, height);
        let targetSize = minSide > MAX_WIDTH ? MAX_WIDTH : minSide; // 如果最小边大于MAX_WIDTH，则压缩至MAX_WIDTH

        // 设置canvas尺寸为目标尺寸
        canvas.width = targetSize;
        canvas.height = targetSize;

        if (ctx) {
          // 填充白色背景（针对透明 PNG）
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, targetSize, targetSize);

          // 计算剪裁起点，确保剪裁区域居中
          const startX = width > height ? (width - height) / 2 : 0;
          const startY = height > width ? (height - width) / 2 : 0;

          // 执行剪裁和压缩
          ctx?.drawImage(img, startX, startY, minSide, minSide, 0, 0, targetSize, targetSize);

          const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
          //判断是有效的base64
          if (!dataUrl.startsWith('data:image')) {
            reject({ message: '无效的base64', errorType: ErrorType.ThumbnailFail });
          }
          const blob = dataURItoBlob(dataUrl);

          const name = fileName.split('.').slice(0, -1).join('.');
          const ext = fileName.split('.').pop();
          const file = blobToFile(blob, `${name}-thumbnail.jpg`, blob.type) as RcFile;
          //上传封面
          // const thumbnailUrl = await service.upload.uploadFileByProxy(file, UploadFileType.Public, false);
          file.uid = uuid();
          um.current.add(file as RcFile, {
            params: {
              acl: FileACL.Public,
              namespace,
              overseas: false,
            },
            onFinished: async (params, file) => {
              if (!params.downloadUrl) {
                reject({ message: '上传封面失败', errorType: ErrorType.ThumbnailFail });
                return;
              }
              resolve({ ...params, fileName: file.name, fileSize: file.size.toString() } as UploadFileObjType);
            },
            onFailed: (error) => {
              reject(error);
            },
          });
        } else {
          reject({ message: '无法获取画布上下文', errorType: ErrorType.ThumbnailFail });
        }
      };

      img.onerror = (error) => reject(error);
      img.src = url;
    });
  };

  const beforeUpload = async (file: UploadFile, fileList: UploadFile[]) => {
    if (!file) {
      return Upload.LIST_IGNORE;
    }

    // 限制文件数量
    if (maxFilesCount && !isNil(maxFilesCount) && value.length + fileList.length > maxFilesCount) {
      AntdMessage.error(`最多上传 ${maxFilesCount} 个文件`);
      return Upload.LIST_IGNORE;
    }

    if (enableVideo) {
      // 只能上传图片和视频
      if (!isImageFile(file.name) && !isMp4(file.name) && !isMov(file.name)) {
        AntdMessage.error('请上传 jpg、png、jpeg、gif 格式图片或 mp4、mov 格式视频');
        return Upload.LIST_IGNORE;
      }
    } else {
      // 只能上传图片
      if (!isImageFile(file.name)) {
        AntdMessage.error('请上传 jpg、png、jpeg、gif 格式图片');
        return Upload.LIST_IGNORE;
      }
    }

    // 限制上传图片大小
    if (isImageFile(file.name) && (!file.size || file.size / 1024 / 1024 > maxImageSize)) {
      AntdMessage.error(`文件大小不能超过 ${maxImageSize}M`);
      return Upload.LIST_IGNORE;
    }

    if (isVideoFile(file.name) && (!file.size || file.size / 1024 / 1024 > maxVideoSize)) {
      AntdMessage.error(`文件大小不能超过 ${maxVideoSize}M`);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file as FileType);
    });
  };

  const onFileChange: UploadProps['onChange'] = async ({ file, fileList }) => {
    onChange?.(file, fileList);
  };

  const checkOcr = async (file: UploadFileObjType, type: 'IMG' | 'TEXT' | 'VIDEO' = 'IMG') => {
    const result = await service.ocr.checkUserInformation({ infoType: type, imgUrl: [file] });
    return result;
  };

  const onUploadFinished = async (params: UploadRequestOption) => {
    const { file, data: uploadData, onError, onSuccess } = params;
    const srcFile = file as RcFile;

    if (!uploadData?.downloadUrl) {
      onError?.({ name: 'upload error', message: '上传失败', status: ErrorType.UploadFail }, srcFile);
      return;
    }

    const fileObj = {
      ...uploadData,
      fileName: srcFile.name,
      fileSize: srcFile.size.toString(),
    } as UploadFileObjType;

    if (isImageFile(srcFile.name)) {
      let thumbUrl = {} as UploadFileObjType;
      const isPass = await checkOcr(fileObj, 'IMG');

      if (shouldGenerateThumb) thumbUrl = await generateThumbUrl(srcFile);

      onSuccess?.({ url: fileObj, previewUrl: thumbUrl, illegal: !isPass, type: 'IMG' });
    } else if (isVideoFile(srcFile.name)) {
      let thumbUrl = {} as UploadFileObjType;
      const isPass = await checkOcr(fileObj, 'VIDEO');

      if (shouldGenerateThumb) thumbUrl = await generateVideoThumbUrl(uploadData.downloadUrl as string, srcFile.name);

      if (uploadData.downloadUrl) {
        onSuccess?.({ url: fileObj, previewUrl: thumbUrl, illegal: !isPass, type: 'VIDEO' });
      } else {
        onError?.({ name: 'upload error', message: '上传失败', status: ErrorType.UploadFail }, srcFile);
      }
    } else {
      // 除去图片和视频，其它类型文件不进行易盾校验和文件处理
      if (uploadData.downloadUrl) {
        onSuccess?.({ url: fileObj, previewUrl: fileObj, illegal: false, type: 'TEXT' });
      } else {
        onError?.({ name: 'upload error', message: '上传失败', status: ErrorType.UploadFail }, srcFile);
      }
    }
  };

  const customRequest = (params: UploadRequestOption) => {
    const { file, onProgress, onError } = params;
    const srcFile = file as RcFile;

    try {
      // onProgress({ percent: 0, status: 'uploading' }, file);
      // const res = await service.upload.uploadFileByProxy(file as any, UploadFileType.Public, false);
      um.current.add(file as RcFile, {
        params: {
          acl: FileACL.Public,
          namespace,
          overseas: false,
        },
        onProgress: (percent) => {
          onProgress?.({ percent });
        },
        onFinished: (result) => {
          onUploadFinished({ ...params, data: result as Record<string, any> });
        },
        onFailed: (error) => {
          onError?.({ name: 'upload error', message: '上传失败', status: ErrorType.UploadFail }, srcFile);
        },
      });
    } catch (e: any) {
      onError?.(e, srcFile);
    }
  };

  const generateThumbUrl = async (file: RcFile): Promise<UploadFileObjType> => {
    const dataUrl = await getBase64(file);
    const thumbObj = await cropImgByCanvas(dataUrl as string, file.name);
    return thumbObj;
  };

  const generateVideoThumbUrl = (fileUrl: string, name): Promise<UploadFileObjType> => {
    return new Promise<UploadFileObjType>((resolve, reject) => {
      //获取视频第10秒的截图
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous'; // 处理跨域问题

      video.addEventListener('loadeddata', () => {
        video.currentTime = 1; // 跳到第1秒
      });

      video.addEventListener('seeked', () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');

        if (ctx) {
          // ctx?.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
          // 确定剪裁和压缩后的目标尺寸
          const width = video.videoWidth,
            height = video.videoHeight;
          const minSide = Math.min(width, height);
          let targetSize = minSide > MAX_WIDTH ? MAX_WIDTH : minSide; // 如果最小边大于MAX_WIDTH，则压缩至MAX_WIDTH

          // 设置canvas尺寸为目标尺寸
          canvas.width = targetSize;
          canvas.height = targetSize;

          // 计算剪裁起点，确保剪裁区域居中
          const startX = width > height ? (width - height) / 2 : 0;
          const startY = height > width ? (height - width) / 2 : 0;

          // 执行剪裁和压缩
          ctx?.drawImage(video, startX, startY, minSide, minSide, 0, 0, targetSize, targetSize);
          const dataUrl = canvas.toDataURL('image/png');
          //判断是有效的base64
          if (!dataUrl.startsWith('data:image')) {
            reject({ message: '无效的base64', errorType: ErrorType.VideoCodec });
          }
          const blob = dataURItoBlob(dataUrl);

          const thumbFile = blobToFile(blob, `${name}-thumbnail.png`, 'image/png');

          Object.assign(thumbFile, { uid: uuid() });
          um.current.add(thumbFile as RcFile, {
            params: {
              acl: FileACL.Public,
              namespace,
              overseas: false,
            },
            onFinished: async (params, file) => {
              if (!params?.fileId) {
                reject({ message: '上传封面失败', errorType: ErrorType.ThumbnailFail });
                return;
              }
              resolve({
                ...params,
                fileName: thumbFile.name,
                fileSize: thumbFile.size.toString(),
              } as UploadFileObjType);
            },
            onFailed: (error) => {
              reject(error);
            },
          });
        } else {
          reject({ message: '无法获取画布上下文', errorType: ErrorType.ThumbnailFail });
        }
      });
      video.addEventListener('error', () => {
        reject({ message: '视频无法播放', errorType: ErrorType.VideoCodec });
      });
      video.src = fileUrl;
    });
  };

  const uploadProps: UploadProps = {
    // accept,
    showUploadList: false,
    multiple: true,
    pastable: true,
    // fileList: value,
    customRequest,
    onChange: onFileChange,
    beforeUpload: customBeforeUpload || beforeUpload,
    ...otherUploadProps,
  };

  return <Dragger id={id} {...uploadProps} children={children} />;
};

export default UploadFileDragger;
